/**
 * B-new-30 v1c-B — referral-code sessionStorage bridge.
 *
 * Same B-new-33 pattern as `auth-redirect.ts`: the referral code that lands
 * on an auth entry URL via `?ref=<code>` needs to survive the OAuth roundtrip
 * (which strips URL params — LinkedIn/Google redirect back to a fixed callback
 * URL without the original query string). We write to sessionStorage at the
 * auth-entry landing, read after successful auth, POST to /api/referral/apply,
 * then clear.
 *
 * Format check: `XE-[A-Z0-9]+-[A-Z0-9]+`, same regex as the server's
 * `processReferral` invalid-format guard (`apps/api/src/routes/referral.ts:247`).
 * Rejecting garbage client-side saves a round-trip; server still enforces on
 * receive, so this is a friction reducer, not a security boundary.
 *
 * The whole module is side-effect-free — call the helpers from the
 * auth-entry components + oauth.callback + sign-up success handlers.
 */

export const REF_CODE_STORAGE_KEY = 'xeito:refCode'

const REF_CODE_FORMAT = /^XE-[A-Z0-9]+-[A-Z0-9]+$/i

export function isValidRefCode(value: unknown): value is string {
  return typeof value === 'string' && REF_CODE_FORMAT.test(value)
}

/**
 * Called on any auth-entry page load. Reads `?ref=<code>` from the URL, and
 * if valid, writes it to sessionStorage so it survives the OAuth roundtrip.
 * Safe to call on every mount — a stored code stays until `clearRefCode()`
 * or the tab closes, so repeated writes with the same value are idempotent.
 */
export function captureRefCodeFromUrl(): void {
  if (typeof window === 'undefined') return
  try {
    const raw = new URLSearchParams(window.location.search).get('ref')
    if (raw && isValidRefCode(raw)) {
      window.sessionStorage.setItem(REF_CODE_STORAGE_KEY, raw)
    }
  } catch {
    // sessionStorage throws in Safari private mode + some cookie-blocking
    // extensions. Silently skip — the referral is a nice-to-have, not
    // load-bearing enough to surface an error to the user.
  }
}

/**
 * Read (do not clear) the stored ref code. Used by the POST-on-auth-success
 * caller so it can inspect + retry if the endpoint returns non-200 before
 * calling `clearRefCode()`.
 */
export function readRefCodeFromSession(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = window.sessionStorage.getItem(REF_CODE_STORAGE_KEY)
    return isValidRefCode(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * Remove the stored ref code. Called after `/api/referral/apply` responds 200
 * (or 401 — an auth failure means we can never apply, no point holding on).
 */
export function clearRefCode(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(REF_CODE_STORAGE_KEY)
  } catch {
    // See captureRefCodeFromUrl — silent per browser-storage-quirk policy.
  }
}

/**
 * The full loop: read + POST + clear. Called after successful authentication
 * on OAuth callback / magic-link success / passkey success.
 *
 * Contract:
 *   - No-op if no valid code is stored.
 *   - Always clears the code after ONE attempt, regardless of endpoint result.
 *     The server endpoint is idempotent so retries are safe, but we don't want
 *     stale codes lingering across future signups on the same tab.
 *   - Fire-and-forget: never throws. A failed POST logs but doesn't block the
 *     post-signup redirect.
 *
 * @param apiUrl - the API base URL (e.g. `https://api.xeito.ai` in prod, from
 *                 `import.meta.env.VITE_API_URL`).
 */
export async function applyStoredRefCode(apiUrl: string): Promise<void> {
  const code = readRefCodeFromSession()
  if (!code) return
  try {
    await fetch(`${apiUrl}/api/referral/apply`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
  } catch (err) {
    // Never block the redirect on a failed referral apply.
    console.warn('[referral.apply] POST failed (non-blocking):', err)
  } finally {
    clearRefCode()
  }
}
